import { useEffect, useState, useRef, useContext, useMemo } from 'react';
import { UserContext } from './UserContext';
import Sidebar from './Sidebar.js';
import { Card, Row, Col, Container, Form, Button, Modal, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import Breadcrumb from './Breadcrumb.js';
import UserAddGeneral from './UserAddGeneral.js';
import UserAddRegion from './UserAddRegion.js';
import UserAddJob from './UserAddJob.js';
import UserAddCompany from './UserAddCompany.js';
import UserAddStudying from './UserAddStudying.js';
import UserAddOccupation from './UserAddOccupation.js';
import api from './api/axiosInstance.js';
import ProfileMobileTabs from './ProfileMobileTabs';
import ProfileUpdateSuccessModal from './components/Profile/ProfileUpdateSuccessModal';
import ProfileAccountSuccessModal, { useProfileAccountSuccess } from './components/Profile/ProfileAccountSuccessModal';
import icSucess from './assets/img/ic-celebration-circle.svg';
import icEye from './assets/img/ic-eye.svg';
import icEyeClosed from './assets/img/ic-eye-closed.svg';
import openPublicPdf from './utils/openPublicPdf';

const profileNavItems = [
    { to: '/profile', eventKey: 'about', label: 'About', end: true },
    { to: '/profile/academics', eventKey: 'academics', label: 'Academics' },
    { to: '/profile/work-preferences', eventKey: 'work', label: 'Work Preferences' },
    { to: '/profile/history', eventKey: 'history', label: 'History of changes' },
];

const UserProfileWorkPreferences = () => {
    const { user: actor, setUser } = useContext(UserContext);
    const id = String(actor?._id || '');
    const navigate = useNavigate();

    const occupationRef = useRef(null);
    const regionRef = useRef();

    const [errors, setErrors] = useState({});
    const [profileImage, setProfileImage] = useState(null);
    const [companies, setCompanies] = useState([]);
    const [showSavedModal, setShowSavedModal] = useState(false);
    const [savedModalMessage, setSavedModalMessage] = useState('');
    const [isLoadingUser, setIsLoadingUser] = useState(true);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showDeactivateModal, setShowDeactivateModal] = useState(false);
    const { accountSuccessKind, showAccountSuccess, hideAccountSuccess } = useProfileAccountSuccess();
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [newPwd, setNewPwd] = useState('');
    const [confirmPwd, setConfirmPwd] = useState('');
    const [showNewPwd, setShowNewPwd] = useState(false);
    const [showConfirmPwd, setShowConfirmPwd] = useState(false);
    const [pwdMsg, setPwdMsg] = useState('');
    const [pwdSuccess, setPwdSuccess] = useState('');
    const [pwdLoading, setPwdLoading] = useState(false);
    const [pwdFading, setPwdFading] = useState(false);

    useEffect(() => {
        if (!pwdSuccess) return;
        setPwdFading(false);
        const fadeTimer = setTimeout(() => setPwdFading(true), 4000);
        const clearTimer = setTimeout(() => { setPwdSuccess(''); setPwdFading(false); }, 5000);
        return () => { clearTimeout(fadeTimer); clearTimeout(clearTimer); };
    }, [pwdSuccess]);

    const pwdChecks = useMemo(() => {
        const p = String(newPwd || '');
        return {
            minLen: p.length >= 8,
            lower: /[a-z]/.test(p),
            upper: /[A-Z]/.test(p),
            number: /\d/.test(p),
            special: /\W/.test(p),
        };
    }, [newPwd]);

    const pwdMatch = useMemo(() => {
        if (!newPwd && !confirmPwd) return null;
        return newPwd === confirmPwd;
    }, [newPwd, confirmPwd]);

    const [generalData, setGeneralData] = useState({
        profileImage: '',
        firstName: '',
        familyName: '',
        gender: '',
        company: '',
        companyRole: '',
    });

    const [regionData, setRegionData] = useState({
        countriesWorkPreferences: [],
        citiesWorkPreferences: [],
        languagesWorkPreferences: [],
    });

    const [jobPreferencesData, setJobPreferencesData] = useState({
        industryJobPreferences: [],
        employmentJobPreferences: '',
        fteJobPreferences: '',
        jobTypeJobPreferences: '',
    });

    const [companyPreferencesData, setCompanyPreferencesData] = useState({
        companyGlobalSize: '',
        companyLocalSize: '',
        companyRating: '',
        fortune: '',
    });

    const [studyingInfoData, setStudyingInfoData] = useState({
        currentDegree: '',
        futureDegree: '',
        plannedGraduationYear: '',
    });

    const [occupationData, setOccupationData] = useState({
        occupations: [{ major: '', subMajor: '', minor: '', unit: '', skill: '' }],
    });

    const normalizeId = (x) => {
        if (!x) return '';
        if (typeof x === 'string') return x;
        if (typeof x === 'object') {
            if (x._id) return normalizeId(x._id);
            if (x.$oid) return String(x.$oid);
            if (x.id) return normalizeId(x.id);
            if (typeof x.toHexString === 'function') return x.toHexString();
            if (typeof x.toString === 'function' && x.toString !== Object.prototype.toString) {
                const s = x.toString();
                if (/^[a-fA-F0-9]{24}$/.test(s)) return s;
            }
        }
        return '';
    };

    useEffect(() => {
        const fetchCompanies = async () => {
            try {
                const res = await api.get('/api/companies');
                setCompanies(res.data);
            } catch (err) {
                console.error('Error fetching companies:', err);
            }
        };
        fetchCompanies();
    }, []);

    useEffect(() => {
        if (!id) return;
        const fetchUserData = async () => {
            setIsLoadingUser(true);
            try {
                const res = await api.get(`/api/users/${id}`);
                const u = res.data;
                if (!u) return;

                setGeneralData({
                    profileImage: u.profileImage || '',
                    firstName: u.firstName || '',
                    familyName: u.familyName || '',
                    gender: u.gender || '',
                    profileImagePreview: u.profileImage || '',
                    company: normalizeId(u.company),
                    companyRole: u.companyRole || '',
                    activityStatus: u.activityStatus || false,
                });

                setRegionData({
                    citiesWorkPreferences: Array.isArray(u.citiesWorkPreferences) ? u.citiesWorkPreferences : (u.citiesWorkPreferences ? [u.citiesWorkPreferences] : []),
                    countriesWorkPreferences: Array.isArray(u.countriesWorkPreferences) ? u.countriesWorkPreferences : (u.countriesWorkPreferences ? [u.countriesWorkPreferences] : []),
                    languagesWorkPreferences: Array.isArray(u.languagesWorkPreferences) ? u.languagesWorkPreferences : (u.languagesWorkPreferences ? [u.languagesWorkPreferences] : []),
                });

                setJobPreferencesData({
                    industryJobPreferences: Array.isArray(u.industryJobPreferences) ? u.industryJobPreferences : (u.industryJobPreferences ? [u.industryJobPreferences] : []),
                    employmentJobPreferences: u.employmentJobPreferences || '',
                    fteJobPreferences: u.fteJobPreferences || '',
                    jobTypeJobPreferences: u.jobTypeJobPreferences || '',
                });

                setCompanyPreferencesData({
                    companyGlobalSize: u.companyGlobalSize || '',
                    companyLocalSize: u.companyLocalSize || '',
                    companyRating: u.companyRating || '',
                    fortune: u.fortune || '',
                });

                setStudyingInfoData({
                    currentDegree: u.currentDegree || '',
                    futureDegree: u.futureDegree || '',
                    plannedGraduationYear: u.plannedGraduationYear || '',
                });

                const toOccRow = (o = {}) => ({
                    major: o.major ?? '',
                    subMajor: o.subMajor ?? '',
                    minor: o.minor ?? '',
                    unit: o.unit ?? '',
                    skill: o.skill ?? '',
                });
                setOccupationData({
                    occupations: Array.isArray(u.occupations) && u.occupations.length
                        ? u.occupations.slice(0, 3).map(toOccRow)
                        : [{ major: '', subMajor: '', minor: '', unit: '', skill: '' }],
                });
            } catch (err) {
                console.error('Error fetching user data:', err);
            } finally {
                setIsLoadingUser(false);
            }
        };
        fetchUserData();
    }, [id]);

    const validateGeneralFields = () => {
        const newErrors = {};
        if (!generalData.firstName.trim()) newErrors.firstName = 'First name is required';
        if (!generalData.familyName.trim()) newErrors.familyName = 'Last name is required';
        if (!generalData.gender) newErrors.gender = 'Gender is required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleGeneralChange = (e) => {
        const { name, value, files } = e.target;
        if (name === 'profileImage' && files?.length > 0) {
            const file = files[0];
            setProfileImage(file);
            setGeneralData((prev) => ({ ...prev, profileImagePreview: URL.createObjectURL(file) }));
            return;
        }
        setGeneralData((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: '' }));
    };

    const handleGeneralSubmit = async (e) => {
        if (e) e.preventDefault();
        if (!validateGeneralFields()) return false;
        try {
            const data = new FormData();
            data.append('firstName', generalData.firstName || '');
            data.append('familyName', generalData.familyName || '');
            data.append('gender', generalData.gender || '');
            if (profileImage) data.append('profileImage', profileImage);
            await api.put(`/api/users/${id}`, data);
            setSavedModalMessage('General information updated successfully.');
            setShowSavedModal(true);
            return true;
        } catch (err) {
            console.error('Error updating general info:', err);
            return false;
        }
    };

    const handleGeneralReset = (resetData) => { setGeneralData(resetData); setErrors({}); };

    const isActive = generalData.activityStatus;

    const handleDeactivate = async () => {
        try {
            const endpoint = isActive ? '/api/users/me/deactivate' : '/api/users/me/activate';
            await api.put(endpoint);
            setShowDeactivateModal(false);
            showAccountSuccess(isActive ? 'deactivated' : 'activated');
            setGeneralData((prev) => ({ ...prev, activityStatus: !isActive }));
        } catch (err) {
            console.error('Error toggling account status:', err);
        }
    };

    const handleDelete = async () => {
        try {
            await api.delete('/api/users/me');
            setShowDeleteModal(false);
            if (setUser) setUser(null);
            navigate('/');
        } catch (err) {
            console.error('Error deleting account:', err);
        }
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        setPwdMsg('');
        setPwdSuccess('');
        if (!newPwd || !confirmPwd) { setPwdMsg('Please enter and confirm your new password.'); return; }
        if (newPwd !== confirmPwd) { setPwdMsg('Passwords do not match.'); return; }
        const passwordRegex = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*\W).{8,}$/;
        if (!passwordRegex.test(newPwd)) { setPwdMsg('Password must meet all requirements below.'); return; }
        try {
            setPwdLoading(true);
            await api.put('/api/users/me/change-password', { newPassword: newPwd });
            setNewPwd(''); setConfirmPwd('');
            setShowPasswordModal(false);
            showAccountSuccess('password');
        } catch (err) {
            setPwdMsg(err.response?.data?.msg || 'Failed to change password.');
        } finally {
            setPwdLoading(false);
        }
    };

    const handleRegionChange = (e) => {
        const { name, value } = e.target;
        setRegionData((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: '' }));
    };

    const handleRegionSubmit = async () => {
        try {
            await api.put(`/api/users/${id}/region`, {
                countriesWorkPreferences: regionData.countriesWorkPreferences,
                citiesWorkPreferences: regionData.citiesWorkPreferences,
                languagesWorkPreferences: regionData.languagesWorkPreferences,
            });
            setSavedModalMessage('Region information updated successfully.');
            setShowSavedModal(true);
        } catch (err) {
            console.error('Error updating region:', err);
        }
    };

    const handleJobPreferencesChange = (e) => {
        const { name, value } = e.target;
        setJobPreferencesData((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: '' }));
    };

    const handleJobPreferencesSubmit = async () => {
        try {
            await api.put(`/api/users/${id}/job-preferences`, {
                industryJobPreferences: jobPreferencesData.industryJobPreferences,
                employmentJobPreferences: jobPreferencesData.employmentJobPreferences,
                fteJobPreferences: jobPreferencesData.fteJobPreferences,
                jobTypeJobPreferences: jobPreferencesData.jobTypeJobPreferences,
            });
            setSavedModalMessage('Job preferences updated successfully.');
            setShowSavedModal(true);
        } catch (err) {
            console.error('Error updating job preferences:', err);
        }
    };

    const handleCompanyPreferencesChange = (e) => {
        const { name, value } = e.target;
        setCompanyPreferencesData((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: '' }));
    };

    const handleCompanyPreferencesSubmit = async () => {
        try {
            await api.put(`/api/users/${id}/company-preferences`, {
                companyGlobalSize: companyPreferencesData.companyGlobalSize,
                companyLocalSize: companyPreferencesData.companyLocalSize,
                companyRating: companyPreferencesData.companyRating,
                fortune: companyPreferencesData.fortune,
            });
            setSavedModalMessage('Company preferences updated successfully.');
            setShowSavedModal(true);
        } catch (err) {
            console.error('Error updating company preferences:', err);
        }
    };

    const handleStudyingInfoChange = (e) => {
        const { name, value } = e.target;
        setStudyingInfoData((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: '' }));
    };

    const handleStudyingInfoSubmit = async () => {
        try {
            await api.put(`/api/users/${id}/studying-info`, {
                currentDegree: studyingInfoData.currentDegree,
                futureDegree: studyingInfoData.futureDegree,
                plannedGraduationYear: studyingInfoData.plannedGraduationYear,
            });
            setSavedModalMessage('Work preferences updated successfully.');
            setShowSavedModal(true);
        } catch (err) {
            console.error('Error updating studying info:', err);
        }
    };

    const handleOccupationSubmit = async (e, listOverride) => {
        if (e?.preventDefault) e.preventDefault();
        try {
            if (occupationRef.current?.validate && !occupationRef.current.validate()) return false;
            await api.put(`/api/users/${id}/occupations`, { occupations: listOverride ?? occupationData.occupations });
            setSavedModalMessage('Occupations updated successfully.');
            setShowSavedModal(true);
            return true;
        } catch (err) {
            console.error('Error updating occupations:', err);
            return false;
        }
    };

    if (isLoadingUser) {
        return (
            <>
                <Sidebar />
                <main className="main-content flex-grow-1 profile-page">
                    <Container className="profile-page__container"><p className="mt-4">Loading...</p></Container>
                </main>
            </>
        );
    }

    return (
        <>
            <Sidebar />
            <main className="main-content flex-grow-1 profile-page">
                <Container className="profile-page__container">
                    {pwdSuccess && (
                        <Row>
                            <Col>
                                <Alert variant="success" className="d-flex flex-row" style={{ transition: 'opacity 1s', opacity: pwdFading ? 0 : 1 }}>
                                    <strong>Password saved</strong>
                                </Alert>
                            </Col>
                        </Row>
                    )}
                    <Row>
                        <Col>
                            <Breadcrumb />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <Card className="profile-content-shell">
                                <Card.Body className="pt-0 profile-mobile">
                                    <Row>
                                        <Col xs={12}>
                                            <Row>
                                                <Col>
                                                    <Form onSubmit={handleGeneralSubmit}>
                                                        <UserAddGeneral
                                                            formData={{ ...generalData }}
                                                            profileImage={generalData.profileImagePreview}
                                                            handleChange={handleGeneralChange}
                                                            handleGeneralSubmit={handleGeneralSubmit}
                                                            errors={errors}
                                                            isEditPage={true}
                                                            handleReset={handleGeneralReset}
                                                            companies={companies}
                                                        />
                                                    </Form>
                                                </Col>
                                            </Row>
                                        </Col>
                                    </Row>
                                    <ProfileMobileTabs items={profileNavItems} />
                                    <Row>
                                        <Col xs={12}>
                                            <div className="profile-mobile-content-card profile-work-sections">
                                            <Row>
                                                <Col>
                                                    <Form onSubmit={handleRegionSubmit}>
                                                        <UserAddRegion
                                                            formData={regionData}
                                                            handleChange={handleRegionChange}
                                                            handleRegionSubmit={handleRegionSubmit}
                                                            errors={errors}
                                                            ref={regionRef}
                                                            isEditPage={true}
                                                        />
                                                    </Form>
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col>
                                                    <Form onSubmit={handleJobPreferencesSubmit}>
                                                        <UserAddJob
                                                            formData={jobPreferencesData}
                                                            handleChange={handleJobPreferencesChange}
                                                            handleJobPreferencesSubmit={handleJobPreferencesSubmit}
                                                            errors={errors}
                                                            isEditPage={true}
                                                        />
                                                    </Form>
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col>
                                                    <Form onSubmit={handleOccupationSubmit}>
                                                        <UserAddOccupation
                                                            ref={occupationRef}
                                                            formData={occupationData}
                                                            onOccupationsChange={(list) =>
                                                                setOccupationData((prev) => ({ ...prev, occupations: list }))
                                                            }
                                                            handleOccupationSubmit={handleOccupationSubmit}
                                                            errors={errors}
                                                            isEditPage={true}
                                                        />
                                                    </Form>
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col>
                                                    <Form onSubmit={handleCompanyPreferencesSubmit}>
                                                        <UserAddCompany
                                                            formData={companyPreferencesData}
                                                            handleChange={handleCompanyPreferencesChange}
                                                            handleCompanyPreferencesSubmit={handleCompanyPreferencesSubmit}
                                                            errors={errors}
                                                            isEditPage={true}
                                                        />
                                                    </Form>
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col>
                                                    <Form onSubmit={handleStudyingInfoSubmit}>
                                                        <UserAddStudying
                                                            formData={studyingInfoData}
                                                            handleChange={handleStudyingInfoChange}
                                                            handleStudyingInfoSubmit={handleStudyingInfoSubmit}
                                                            errors={errors}
                                                            isEditPage={true}
                                                        />
                                                    </Form>
                                                </Col>
                                            </Row>
                                            </div>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col className="d-flex justify-content-end gap-3 font-size-md profile-legal-links">
                                            <a className="text-primary fw-bold" href="/Abroadify-Privacy-Notice.pdf" target="_blank" rel="noopener noreferrer" onClick={(event) => openPublicPdf(event, '/Abroadify-Privacy-Notice.pdf')}>Privacy</a>
                                            <span className="profile-legal-links__separator d-lg-none" aria-hidden="true">·</span>
                                            <a className="text-primary fw-bold" href="/Abroadify-Terms-of-Use.pdf" target="_blank" rel="noopener noreferrer" onClick={(event) => openPublicPdf(event, '/Abroadify-Terms-of-Use.pdf')}>Terms of Use</a>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                    <div className="buttons-row profile-account-actions">
                        <Row>
                            <Col className="d-flex gap-3 justify-content-end">
                                <Button variant="outline-danger" onClick={() => setShowDeleteModal(true)}>Delete account</Button>
                                <Button variant="outline-secondary" onClick={() => setShowDeactivateModal(true)}>{isActive ? 'Deactivate account' : 'Activate account'}</Button>
                                <Button variant="outline-secondary" onClick={() => { setShowPasswordModal(true); setPwdMsg(''); setPwdSuccess(''); setNewPwd(''); setConfirmPwd(''); }}>Change password</Button>
                            </Col>
                        </Row>
                    </div>
                </Container>
            </main>

            <ProfileUpdateSuccessModal show={showSavedModal} onHide={() => setShowSavedModal(false)} message={savedModalMessage} icon={icSucess} />
            <ProfileAccountSuccessModal kind={accountSuccessKind} onHide={hideAccountSuccess} />

            <Modal show={showDeactivateModal} onHide={() => setShowDeactivateModal(false)} centered className="profile-account-mobile-modal">
                <Modal.Header closeButton>
                    <Modal.Title>{isActive ? 'Deactivate account' : 'Activate account'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>{isActive
                        ? 'Are you sure you want to deactivate your account?'
                        : 'Are you sure you want to reactivate your account?'
                    }</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary me-3" onClick={() => setShowDeactivateModal(false)}>Cancel</Button>
                    <Button variant="primary" onClick={handleDeactivate}>{isActive ? 'Deactivate' : 'Activate'}</Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered className="profile-account-mobile-modal">
                <Modal.Header closeButton>
                    <Modal.Title>Delete account</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Are you sure you want to permanently delete your account? This action cannot be undone.</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary me-3" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
                    <Button variant="danger" onClick={handleDelete}>Delete account</Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showPasswordModal} onHide={() => setShowPasswordModal(false)} centered className="profile-account-mobile-modal">
                <Modal.Header closeButton>
                    <Modal.Title>Change password</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleChangePassword}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>New password</Form.Label>
                            <div style={{ position: 'relative' }}>
                                <Form.Control
                                    type={showNewPwd ? 'text' : 'password'}
                                    value={newPwd}
                                    onChange={(e) => { setNewPwd(e.target.value); setPwdMsg(''); }}
                                    autoComplete="new-password"
                                    placeholder="Enter new password"
                                    style={{ paddingRight: '42px' }}
                                />
                                <img src={showNewPwd ? icEye : icEyeClosed} alt="" width={16} height={16}
                                    onClick={() => setShowNewPwd(v => !v)}
                                    style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', opacity: 0.7 }}
                                />
                            </div>
                        </Form.Group>
                        <Form.Group className="mb-2">
                            <Form.Label>Confirm new password</Form.Label>
                            <div style={{ position: 'relative' }}>
                                <Form.Control
                                    type={showConfirmPwd ? 'text' : 'password'}
                                    value={confirmPwd}
                                    onChange={(e) => { setConfirmPwd(e.target.value); setPwdMsg(''); }}
                                    autoComplete="new-password"
                                    placeholder="Repeat new password"
                                    style={{ paddingRight: '42px' }}
                                />
                                <img src={showConfirmPwd ? icEye : icEyeClosed} alt="" width={16} height={16}
                                    onClick={() => setShowConfirmPwd(v => !v)}
                                    style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', cursor: 'pointer', opacity: 0.7 }}
                                />
                            </div>
                        </Form.Group>
                        <div className="mt-3">
                            <div className="mb-2 font-size-sm">Create password that contains at least:</div>
                            <div className="row g-2 font-size-sm">
                                <div className="col-6"><div className={pwdChecks.minLen ? 'text-success' : 'text-danger'}>{pwdChecks.minLen ? '✓' : '✕'} min. 8 characters</div></div>
                                <div className="col-6"><div className={pwdChecks.lower ? 'text-success' : 'text-danger'}>{pwdChecks.lower ? '✓' : '✕'} one lowercase letter</div></div>
                                <div className="col-6"><div className={pwdChecks.upper ? 'text-success' : 'text-danger'}>{pwdChecks.upper ? '✓' : '✕'} one capital letter</div></div>
                                <div className="col-6"><div className={pwdChecks.number ? 'text-success' : 'text-danger'}>{pwdChecks.number ? '✓' : '✕'} one number</div></div>
                                <div className="col-12"><div className={pwdChecks.special ? 'text-success' : 'text-danger'}>{pwdChecks.special ? '✓' : '✕'} one special character</div></div>
                                {pwdMatch !== null && (
                                    <div className="col-12"><div className={pwdMatch ? 'text-success' : 'text-danger'}>{pwdMatch ? '✓' : '✕'} passwords match</div></div>
                                )}
                            </div>
                        </div>
                        {pwdMsg && <div className="mt-3 font-size-sm text-danger">{pwdMsg}</div>}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button className="me-3" variant="outline-secondary" onClick={() => setShowPasswordModal(false)}>Cancel</Button>
                        <Button variant="primary" type="submit" disabled={pwdLoading}>{pwdLoading ? 'Saving…' : 'Save'}</Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </>
    );
};

export default UserProfileWorkPreferences;
